'use client';

import { useRef, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Search, PenTool, Zap, Rocket } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const easing = [0.22, 1, 0.36, 1] as const;

const steps = [
  {
    n: '01',
    icon: Search,
    tag: 'Discovery',
    title: 'Capire\nprima di tutto',
    body: 'Entriamo nei dettagli del tuo lavoro. Dove si perde tempo, dove nascono errori, dove c\'è un margine non ancora sfruttato. In 2-3 sessioni, arriviamo al cuore del problema.',
    detail: ['Workshop di processo', 'Mappatura dei flussi reali', 'KPI chiari prima di scrivere codice'],
    color: '#E62B36',
    bg: 'rgba(230,43,54,0.04)',
  },
  {
    n: '02',
    icon: PenTool,
    tag: 'Design',
    title: 'Progettare\nsenza sorprese',
    body: 'Stack, integrazioni, perimetro — tutto definito prima di iniziare. Il piano tecnico è condiviso e firmato da entrambi. Niente ambiguità, niente improvvisazioni a metà strada.',
    detail: ['Stack e integrazioni definite insieme', 'Wireframe funzionali da validare', 'Roadmap di rilascio trasparente'],
    color: '#8B5CF6',
    bg: 'rgba(139,92,246,0.04)',
  },
  {
    n: '03',
    icon: Zap,
    tag: 'Build',
    title: 'Costruire.\nRilasciare.\nMigliorare.',
    body: 'Sprint brevi, rilasci frequenti. Ogni due settimane hai qualcosa di reale in mano — non slide, non mock. Codice vivo, funzionante, pronto per il tuo feedback.',
    detail: ['Sprint di 2 settimane', 'Demo funzionante ad ogni ciclo', 'Feedback integrato subito'],
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.04)',
  },
  {
    n: '04',
    icon: Rocket,
    tag: 'Launch',
    title: 'Live.\nE sempre\nmigliore.',
    body: 'Il lancio è solo l\'inizio. Monitoriamo, ottimizziamo e miglioriamo sulla base dei dati reali. Perché il software migliore non è quello che consegni — è quello che evolve con te.',
    detail: ['Deploy su infrastruttura dedicata', 'Monitoraggio proattivo attivo', 'Ottimizzazione continua data-driven'],
    color: '#10B981',
    bg: 'rgba(16,185,129,0.04)',
  },
];

export default function ProcessHorizontal() {
  const sectionRef = useRef<HTMLElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const dotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Set initial states: first step visible, rest hidden below
      stepRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.set(el, { opacity: i === 0 ? 1 : 0, yPercent: i === 0 ? 0 : 5 });
      });

      // One full viewport height of scroll per step transition.
      // pinSpacing:"margin" adds margin-bottom directly to the section so it
      // pushes siblings down correctly even inside a flex-col parent.
      const totalScroll = window.innerHeight * (steps.length - 1);

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: `+=${totalScroll}`,
          pin: true,
          pinSpacing: 'margin',
          scrub: 1.2,
          invalidateOnRefresh: true,
          snap: {
            snapTo: 1 / (steps.length - 1),
            duration: { min: 0.3, max: 0.6 },
            delay: 0.08,
            ease: 'power2.inOut',
          },
          onUpdate(self) {
            const active = Math.min(
              steps.length - 1,
              Math.round(self.progress * (steps.length - 1)),
            );
            dotRefs.current.forEach((dot, i) => {
              if (!dot) return;
              dot.style.width = i === active ? '2rem' : '0.75rem';
              dot.style.opacity = i <= active ? '1' : '0.25';
              dot.style.background = steps[i].color;
            });
          },
        },
      });

      // Each step occupies 1 timeline unit. Transitions stagger slightly.
      for (let i = 0; i < steps.length - 1; i++) {
        const cur = stepRefs.current[i];
        const nxt = stepRefs.current[i + 1];
        if (!cur || !nxt) continue;
        tl
          .to(cur, { opacity: 0, yPercent: -5, duration: 0.45 }, i + 0.1)
          .fromTo(nxt, { opacity: 0, yPercent: 5 }, { opacity: 1, yPercent: 0, duration: 0.45 }, i + 0.3);
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [reduce]);

  /* ── Reduced-motion: static vertical list ── */
  if (reduce) {
    return (
      <section className="bg-gray-950 py-24 border-t border-white/[0.05]">
        <div className="mx-auto max-w-5xl px-6 lg:px-12">
          <SectionLabel />
          <div className="mt-20 space-y-20">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.n} className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16 lg:items-start">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: `${step.color}80` }}>
                        {step.n}
                      </span>
                      <div className="flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: `${step.color}30`, background: step.bg }}>
                        <Icon className="h-3.5 w-3.5" style={{ color: step.color }} />
                        <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: `${step.color}AA` }}>
                          {step.tag}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-4xl font-black tracking-tight text-white whitespace-pre-line leading-[1.05]">
                      {step.title}
                    </h3>
                  </div>
                  <div>
                    <p className="text-sm leading-7 text-gray-400 mb-6">{step.body}</p>
                    <ul className="space-y-3">
                      {step.detail.map((d) => (
                        <li key={d} className="flex items-center gap-3 text-xs text-gray-600">
                          <div className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: step.color }} />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="relative bg-gray-950 border-t border-white/[0.05]"
      style={{ height: '100dvh' }}
    >
      {/* Section label */}
      <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none">
        <div className="mx-auto max-w-5xl px-6 py-8 lg:px-12">
          <SectionLabel />
        </div>
      </div>

      {/* Stacked step slides */}
      <div className="relative h-full overflow-hidden">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div
              key={step.n}
              ref={(el) => { stepRefs.current[i] = el; }}
              className="absolute inset-0 flex items-center"
              style={{ paddingTop: '5rem' }}
            >
              <div className="mx-auto w-full max-w-5xl px-6 lg:px-12">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-20 lg:items-center">

                  {/* Left: number, tag, title */}
                  <div>
                    <div className="flex items-center gap-4 mb-8">
                      <span
                        className="font-mono text-[10px] font-bold uppercase tracking-[0.25em]"
                        style={{ color: `${step.color}80` }}
                      >
                        {step.n}
                      </span>
                      <div
                        className="flex items-center gap-2 rounded-full border px-3 py-1"
                        style={{ borderColor: `${step.color}30`, background: step.bg }}
                      >
                        <Icon className="h-3.5 w-3.5" style={{ color: step.color }} />
                        <span
                          className="text-[10px] font-semibold uppercase tracking-widest"
                          style={{ color: `${step.color}AA` }}
                        >
                          {step.tag}
                        </span>
                      </div>
                    </div>

                    <div
                      className="h-px w-12 mb-8"
                      style={{ background: `linear-gradient(to right, ${step.color}, transparent)` }}
                    />

                    <h3 className="text-5xl font-black tracking-tight text-white leading-[1.02] whitespace-pre-line xl:text-6xl">
                      {step.title}
                    </h3>
                  </div>

                  {/* Right: body + bullets */}
                  <div>
                    <p className="text-base leading-7 text-gray-400 mb-10">{step.body}</p>
                    <ul className="space-y-4">
                      {step.detail.map((d) => (
                        <li key={d} className="flex items-center gap-3 text-sm text-gray-500">
                          <div
                            className="h-1.5 w-1.5 rounded-full shrink-0"
                            style={{ background: step.color }}
                          />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Ghost number (desktop only) */}
              <div
                className="absolute right-4 bottom-16 font-black leading-none select-none pointer-events-none hidden lg:block"
                style={{ fontSize: 'clamp(8rem, 18vw, 14rem)', color: `${step.color}08` }}
                aria-hidden
              >
                {step.n}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5">
        {steps.map((step, i) => (
          <div
            key={step.n}
            ref={(el) => { dotRefs.current[i] = el; }}
            className="h-[3px] rounded-full transition-all duration-200"
            style={{
              width: i === 0 ? '2rem' : '0.75rem',
              background: step.color,
              opacity: i === 0 ? 1 : 0.25,
            }}
          />
        ))}
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 right-6 z-20 flex items-center gap-2 text-gray-700 lg:right-12">
        <span className="text-[10px] font-mono uppercase tracking-widest">Scorri</span>
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path d="M8 3v10M4 9l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </section>
  );
}

function SectionLabel() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: easing }}
      className="flex items-baseline gap-6"
    >
      <h2 className="text-lg font-bold text-white">Il metodo Refineed</h2>
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-600">
        Scorri ↓
      </span>
    </motion.div>
  );
}
